/**
 * Service API
 *
 * Low-level API calls for Service entity.
 * This is the infrastructure layer - handles HTTP requests.
 *
 * Note: This file coexists with the legacy src/services/admin/services-service.js
 * to allow gradual migration. New code should use this file.
 */
import api from './axios';
import { isPrivateProviderContext, privateManagementList, privateManagementRequest } from '@/services/admin/private-provider-management';
// =============================================================================
// SERVICE API
// =============================================================================
export const serviceApi = {
    /**
     * Get all services with optional filtering
     * @param params - Optional query parameters for filtering
     * @param params.createdBy - Filter by creator user ID (created_by.id)
     * @returns Response includes photo_urls (array of temporary presigned URLs) and photo_storage_ids
     */
    getAllServices(params) {
        return isPrivateProviderContext() ? privateManagementList('services', params) : api.get('/service', { params });
    },
    /**
     * Get single service by ID
     * Response includes photo_urls (temporary presigned URLs) and photo_storage_ids
     */
    getService(id) {
        return isPrivateProviderContext() ? privateManagementRequest('get', `services/${id}`) : api.get(`/service/${id}`);
    },
    /**
     * Create new service
     * @param serviceData - Service data including photo_storage_ids array
     * @returns API response with created service data including photo_urls
     */
    saveService(serviceData) {
        if (isPrivateProviderContext())
            return privateManagementRequest('post', 'services', stripClientAuthority(serviceData));
        return api.post('/service', stripClientAuthority(serviceData), {
            headers: {
                'Content-Type': 'application/json',
            },
        });
    },
    /**
     * Update existing service
     * @param id - Service ID
     * @param serviceData - Updated service data
     * @returns API response with updated service data including fresh photo_urls
     */
    updateService(id, serviceData) {
        if (isPrivateProviderContext()) {
            if (!Number.isInteger(serviceData.version))
                throw new Error('missing_service_revision');
            return privateManagementRequest('patch', `services/${id}`, stripClientAuthority(serviceData));
        }
        return api.patch(`/service/${id}`, stripClientAuthority(serviceData), {
            headers: {
                'Content-Type': 'application/json',
            },
        });
    },
    /**
     * Delete service
     * This will also handle deletion of associated photos in Firebase
     */
    deleteService(id) {
        return isPrivateProviderContext() ? privateManagementRequest('delete', `services/${id}`) : api.delete(`/service/${id}`);
    },
};
function stripClientAuthority(payload) {
    const { created_by, updated_by, org_id, ...cleanPayload } = payload;
    void created_by;
    void updated_by;
    void org_id;
    return cleanPayload;
}
// =============================================================================
// DEFAULT EXPORT
// =============================================================================
export default serviceApi;
